import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { idempotencyKey } from './idempotency'

/**
 * `admin#plans` and `admin#checkout`, on S-B6's endpoints.
 *
 * **The currency toggle is real, not decorative**, which is what that screen's
 * *"why this shape"* note asks for: *"a tenant seeing ₹ when they expected $
 * needs to fix it in one click, not email support"*. Switching re-fetches, so
 * the prices shown are §11's two price sets rather than a conversion done in
 * the browser — and the caption says how the default was arrived at, because a
 * tenant told they were "detected" when nothing was detected has been told
 * something false about their own data.
 *
 * **The five limits are the whole list.** S-B12a found twelve invented details
 * on this screen — keyword caps, an included usage cap, "priority support", a
 * fourth SKU — and removed all of them. What is rendered here is exactly what
 * `GET /billing/plans` returns, which is exactly REQUIREMENTS §11's table.
 *
 * **Checkout is a top-level navigation to the provider's hosted page.** No card
 * field appears in AugRank's DOM: that keeps AugRank outside PCI DSS scope, and
 * it is the only place an Indian e-mandate can be authenticated at all.
 */
export function PlansPage(): JSX.Element {
  const { role } = useActiveTenant()
  const toast = useToast()
  const navigate = useNavigate()
  const [currency, setCurrency] = useState<'INR' | 'USD' | null>(null)
  const manages = may(role, 'billing:manage')

  const plans = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/billing/plans', {
          params: { query: currency ? { currency } : {} },
          signal,
        }),
      ),
    [currency],
  )
  const current = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/subscription', { signal })),
    [],
  )

  const start = useMutation((plan: 'starter' | 'growth' | 'scale') =>
    call(() =>
      api.POST('/api/v1/billing/subscription', {
        body: { plan, currency: currency },
        params: { header: { 'Idempotency-Key': idempotencyKey('checkout', plan) } },
      }),
    ),
  )
  const change = useMutation((plan: 'starter' | 'growth' | 'scale') =>
    call(() =>
      api.POST('/api/v1/billing/subscription/plan', {
        body: { plan, acknowledge_conflicts: false },
        params: { header: { 'Idempotency-Key': idempotencyKey('plan', plan) } },
      }),
    ),
  )

  if (plans.state.status === 'loading') return <Loading label="Loading plans" />
  if (plans.state.status === 'error') {
    return <ErrorState failure={plans.state.failure} onRetry={plans.refetch} />
  }

  const ladder = plans.state.data
  const subscription = current.state.data?.subscription ?? null

  async function choose(plan: 'starter' | 'growth' | 'scale'): Promise<void> {
    if (subscription) {
      const result = await change.run(plan)
      if (result.ok) {
        toast.show({ kind: 'success', title: 'Your plan has been changed.' })
        void navigate('/billing')
      }
      return
    }
    const result = await start.run(plan)
    if (result.ok) {
      // A top-level navigation rather than an iframe: the edge sets
      // `frame-ancestors 'none'`, and a payment page inside a frame is the
      // shape phishing takes anyway.
      window.location.assign(result.data.checkout_url)
    }
  }

  return (
    <div className="ar-stack ar-stack--loose" style={{ maxWidth: '64rem', margin: '0 auto' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 className="ar-display">Simple pricing</h1>
        <p className="ar-muted" style={{ maxWidth: '44ch', margin: 'var(--ar-space-3) auto 0' }}>
          SEO and outreach together. No separate SKUs, no per-seat pricing.
        </p>
        <div
          className="ar-row"
          style={{ justifyContent: 'center', marginTop: 'var(--ar-space-5)' }}
        >
          <div className="sc-seg" style={{ width: 'auto' }} role="group" aria-label="Currency">
            <button
              type="button"
              aria-pressed={ladder.currency === 'INR'}
              onClick={() => {
                setCurrency('INR')
              }}
            >
              ₹ INR
            </button>
            <button
              type="button"
              aria-pressed={ladder.currency === 'USD'}
              onClick={() => {
                setCurrency('USD')
              }}
            >
              $ USD
            </button>
          </div>
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            {describeSignal(ladder.signal, ladder.country)}
          </span>
        </div>
      </div>

      <InlineError failure={start.state.failure ?? change.state.failure} />

      <div className="ad-plans">
        {ladder.plans.map((plan) => {
          const isCurrent = subscription?.plan_code === plan.code
          return (
            <div
              className={`ar-card ad-plan${plan.code === 'growth' ? ' ad-plan--pick' : ''}`}
              key={plan.code}
            >
              <div className="ar-card__header">
                <h3>{plan.name}</h3>
                {isCurrent ? (
                  <>
                    <span className="ar-card__spacer" />
                    <span className="ar-badge ar-badge--success">Current</span>
                  </>
                ) : null}
              </div>
              <div className="ar-card__body ar-stack">
                <div>
                  <span className="ad-plan__price">{plan.price.display}</span>{' '}
                  <span className="ar-muted">/month</span>
                </div>
                <ul>
                  <li>
                    {plan.sites} {plan.sites === 1 ? 'site' : 'sites'}
                  </li>
                  <li>
                    {plan.linkedin_accounts} LinkedIn{' '}
                    {plan.linkedin_accounts === 1 ? 'account' : 'accounts'}
                  </li>
                  <li>{plan.messages_per_month.toLocaleString()} messages a month</li>
                  <li>{plan.articles_per_month} articles a month</li>
                  <li>
                    {plan.scrapes_per_week === 7
                      ? 'Daily lead discovery'
                      : `Lead discovery ${String(plan.scrapes_per_week)}× a week`}
                  </li>
                </ul>
              </div>
              <div className="ar-card__footer">
                <Button
                  tone={plan.code === 'growth' ? 'primary' : 'ghost'}
                  block
                  disabled={
                    isCurrent || !manages || !plan.available || start.pending || change.pending
                  }
                  onClick={() => {
                    void choose(plan.code as 'starter' | 'growth' | 'scale')
                  }}
                >
                  {buttonLabel(plan.name, isCurrent, plan.available, subscription !== null)}
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      <p
        className="ar-subtle"
        style={{ textAlign: 'center', fontSize: 'var(--ar-text-body-sm-size)' }}
      >
        The five limits above are the whole list. Rank data, model calls, images and proxy bandwidth
        are metered against the monthly budget cap you set in Settings.{' '}
        {ladder.currency === 'INR' ? (
          <>
            Prices exclude {ladder.gst_percent}% GST. Renewals above ₹
            {(ladder.emandate_ceiling_minor / 100).toLocaleString('en-IN')} need your bank&apos;s
            approval each month, which we will remind you about 24 hours before.
          </>
        ) : (
          <>
            Billed by Paddle as merchant of record, which charges and remits your local tax itself.
          </>
        )}{' '}
        Every plan starts with a {ladder.trial_days}-day trial.
      </p>
      {!manages ? (
        <p className="ar-subtle" style={{ textAlign: 'center' }}>
          Only the organisation&apos;s owner can start or change a subscription.
        </p>
      ) : null}
    </div>
  )
}

/**
 * The caption beside the toggle. `admin#plans` prints "Detected: India"; this
 * says that only when something actually was detected.
 */
function describeSignal(signal: string, country: string | null | undefined): string {
  if (signal === 'edge' && country) return `Detected: ${country}`
  if (signal === 'mobile') return 'Based on your mobile number'
  if (signal === 'requested') return 'Your choice'
  return 'Showing rupees by default'
}

function buttonLabel(
  name: string,
  isCurrent: boolean,
  available: boolean,
  subscribed: boolean,
): string {
  if (isCurrent) return 'Your current plan'
  if (!available) return 'Not available yet'
  return subscribed ? `Move to ${name}` : `Choose ${name}`
}
